import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/ui/pagination'
import { TestCaseStatusBadge } from '@/components/projects/TestCaseStatusBadge'
import { useTestCases } from '@/hooks/useTestCases'
import { useDebounce, usePagination } from '@/hooks'
import { getInitials } from '@/lib/utils'
import { formatDateTime } from '@/lib/timeFormatters'
import { testCaseStatusFilterOptions } from '@/types'
import type { TestCase, TestCaseFilters } from '@/types'

interface TestCasesTabProps {
  projectId: string
  workItemId: string
  onNavigate?: () => void
}

export function TestCasesTab({ projectId, workItemId, onNavigate }: TestCasesTabProps) {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()

  const debouncedSearch = useDebounce(search, 300)

  const filters = useMemo(() => {
    const f: TestCaseFilters = {}
    if (debouncedSearch) f.search = debouncedSearch
    if (statuses.length > 0) f.statuses = statuses
    return Object.keys(f).length > 0 ? f : undefined
  }, [debouncedSearch, statuses])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, statuses, resetPage])

  const { data, isLoading } = useTestCases(projectId, workItemId, paginationParams, filters)
  const testCases = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-medium text-card-foreground">Test Cases</h2>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by test case title, ID, or assignee"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            aria-label="Search test cases"
          />
        </div>
        <MultiSelect
          options={testCaseStatusFilterOptions}
          value={statuses}
          onChange={setStatuses}
          placeholder="All Statuses"
          displayMode="compact"
          searchable={false}
          showCompactIcon={false}
          showCompactOverflowCount
          className="w-[200px] shrink-0"
          aria-label="Filter by status"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading test cases">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : totalCount === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="test-cases-empty">
          No test cases match your filters.
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Test cases table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">#</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="w-[130px]">Status</TableHead>
                <TableHead className="w-[190px]">Last Run</TableHead>
                <TableHead className="w-[220px]">User</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {testCases.map((tc) => (
                <TestCaseRow
                  key={tc.id}
                  testCase={tc}
                  onClick={() => {
                    onNavigate?.()
                    navigate(
                      `/projects/${projectId}/testing-integration/test-suites/${tc.testSuiteId}?testCaseId=${tc.id}`,
                    )
                  }}
                />
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  )
}

interface TestCaseRowProps {
  testCase: TestCase
  onClick: () => void
}

function TestCaseRow({ testCase, onClick }: TestCaseRowProps) {
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onClick}
      data-testid={`test-case-row-${testCase.testCaseIdentifier}`}
    >
      <TableCell className="font-medium text-primary">{testCase.testCaseIdentifier}</TableCell>
      <TableCell>
        {testCase.title}
      </TableCell>
      <TableCell><TestCaseStatusBadge status={testCase.status} /></TableCell>
      <TableCell className="text-sm">{testCase.lastRunAt ? formatDateTime(testCase.lastRunAt) : '\u2014'}</TableCell>
      <TableCell>
        {testCase.createdByUserName && (
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium shrink-0">
              {getInitials(testCase.createdByUserName)}
            </div>
            <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
              <span className="text-sm font-medium">{testCase.createdByUserName}</span>
              {testCase.createdByUserEmail && (
                <span className="text-xs text-muted-foreground">{testCase.createdByUserEmail}</span>
              )}
            </div>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}
